<script setup lang="ts">
import { ref, computed } from 'vue'
import { useFormStore } from '@/stores/form'

const emit = defineEmits<{
  (e: 'submit'): void
  (e: 'validate'): void
}>()

const formStore = useFormStore()

const email = ref(formStore.data.email || '')
const showValidation = ref(false)

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

const isValidFormat = computed(() => {
  return emailRegex.test(email.value.trim())
})

const isChecking = computed(() => formStore.validation.email.checking)
const validationError = computed(() => formStore.validation.email.error)

async function handleSubmit() {
  if (!email.value.trim()) {
    showValidation.value = true
    return
  }

  if (!isValidFormat.value) {
    showValidation.value = true
    return
  }

  formStore.setField('email', email.value.trim())

  // Emit validate to trigger external validation
  emit('validate')
}

function handleKeydown(e: KeyboardEvent) {
  if (e.key === 'Enter') {
    handleSubmit()
  }
}
</script>

<template>
  <div class="w-full animate-slide-up">
    <div class="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mx-1">
      <!-- Email input -->
      <div>
        <input
          v-model="email"
          type="email"
          placeholder="email@example.com"
          autocomplete="email"
          inputmode="email"
          :disabled="isChecking"
          @keydown="handleKeydown"
          :class="[
            'w-full py-3.5 px-4 border-2 rounded-xl text-base transition-colors',
            (showValidation && (!email.trim() || !isValidFormat)) || validationError
              ? 'border-red-400 bg-red-50'
              : 'border-gray-200 focus:border-primary'
          ]"
        />
        <p v-if="showValidation && !email.trim()" class="text-red-500 text-xs mt-1 ml-1">
          Please enter your email address
        </p>
        <p v-else-if="showValidation && !isValidFormat" class="text-red-500 text-xs mt-1 ml-1">
          Please enter a valid email address
        </p>
        <p v-else-if="validationError" class="text-red-500 text-xs mt-1 ml-1">
          {{ validationError }}
        </p>
      </div>

      <!-- Helper text -->
      <p class="text-xs text-gray-500 mt-2 ml-1">
        We'll only send you valuable information related to your needs.
      </p>

      <!-- Submit button -->
      <button
        @click="handleSubmit"
        :disabled="isChecking"
        class="w-full mt-5 py-4 bg-primary text-white rounded-full font-semibold text-base shadow-lg shadow-primary/30 hover:bg-primary-dark active:scale-[0.98] transition-all disabled:opacity-70 disabled:cursor-not-allowed"
      >
        <template v-if="isChecking">
          <svg class="animate-spin inline w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Verifying...
        </template>
        <template v-else>
          Continue
          <svg class="inline w-5 h-5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </template>
      </button>
    </div>
  </div>
</template>
